import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftRight,
  Calculator,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  HelpCircle,
  Landmark,
  ReceiptText,
  Repeat,
  Tags,
  TrendingUp,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { CanonicalFinancialCommandService } from "@/lib/canonicalFinancialCommandService";
import { CreditCardApplicationService } from "@/lib/creditCardApplicationService";
import { formatBrl, formatDate, getInstallmentAmounts, shiftMonth } from "@/lib/financialDashboardHelpers";
import {
  publishFinancialExperienceUpdate,
  type FinancialChangeAction,
} from "@/lib/financialExperience";

const { createPurchase: createCreditCardPurchaseWithInvoices } =
  CreditCardApplicationService;

export type FinancialModalAction =
  | "expense"
  | "quick_expense"
  | "card_expense"
  | "card"
  | "income"
  | "transfer";

type ActiveModal = Exclude<FinancialModalAction, "card"> | null;
type RecurrenceMode = "single" | "repeat" | "monthly";
type ExpenseRepeatKind = "repeat" | "installment";
type Periodicity = "monthly" | "weekly" | "yearly";
type AmountMode = "total" | "installment";
type QuickAfterSave = "none" | "edit" | "details";

type PersonalAccount = {
  id: string;
  name: string;
  institution: string | null;
  current_balance: number | string;
};

type PersonalCreditCard = {
  id: string;
  name: string;
  bank_name: string | null;
  closing_day: number | string;
  due_day: number | string;
  is_archived?: boolean | null;
};

type ExpensePayload = {
  description: string;
  amount: number;
  category: string;
  subcategory: string;
  dueDate: string;
  transactionDate: string;
  transactionTime: string;
  isPaid: boolean;
  accountId: string;
  recurrenceMode: RecurrenceMode;
  repeatKind: ExpenseRepeatKind;
  repeatQuantity: number;
  periodicity: Periodicity;
  installments: number;
  installmentStart: number;
  amountMode: AmountMode;
};

type IncomePayload = {
  description: string;
  amount: number;
  category: string;
  subcategory: string;
  dueDate: string;
  transactionDate: string;
  transactionTime: string;
  isPaid: boolean;
  accountId: string;
  recurrenceMode: RecurrenceMode;
  repeatQuantity: number;
  periodicity: Periodicity;
};

type CardExpensePayload = {
  description: string;
  amount: number;
  category: string;
  subcategory: string;
  cardId: string;
  purchaseDate: string;
  invoiceMonth: string;
  installments: number;
  installmentStart: number;
  amountMode: AmountMode;
};

type TransferPayload = {
  description: string;
  amount: number;
  fromAccountId: string;
  toAccountId: string;
  dueDate: string;
  transactionDate: string;
  transactionTime: string;
  isPaid: boolean;
  recurrenceMode: RecurrenceMode;
  repeatQuantity: number;
  periodicity: Periodicity;
};

const FINANCIAL_EVENT = "metajuntos:financial-quick-action";

const SAO_PAULO_TIME_ZONE = "America/Sao_Paulo";

const todayIso = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
const currentTime = () => {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};
const currentMonth = () => todayIso().slice(0, 7);
const dateToMonth = (value: string) => value.slice(0, 7);
const toTransactionAt = (date: string, time: string) =>
  `${date}T${time || "12:00"}:00-03:00`;

function normalizeAction(action?: string): ActiveModal {
  if (action === "card") return "card_expense";
  if (
    action === "expense" ||
    action === "quick_expense" ||
    action === "card_expense" ||
    action === "income" ||
    action === "transfer"
  ) {
    return action;
  }
  return null;
}

function amountFromInput(value: string) {
  return Number(value.replace(/\./g, "").replace(",", "."));
}

function amountToInput(value: number) {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function shiftDate(value: string, amount: number, periodicity: Periodicity) {
  if (periodicity === "monthly") return `${shiftMonth(dateToMonth(value), amount)}-${value.slice(8, 10)}`;
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + amount * (periodicity === "weekly" ? 7 : 365));
  return date.toISOString().slice(0, 10);
}

function getRepeatCount(mode: RecurrenceMode, quantity: number) {
  if (mode === "single") return 1;
  if (mode === "monthly") return 12;
  return Math.max(1, quantity);
}

function fieldClass(extra = "") {
  return `h-11 w-full rounded-xl border border-white/10 bg-black/20 px-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-gold/50 ${extra}`;
}

function selectClass(extra = "") {
  return `h-11 w-full rounded-xl border border-white/10 bg-[oklch(0.105_0.012_260)] px-3 text-sm text-white outline-none transition-colors focus:border-gold/50 ${extra}`;
}

export function FinancialModalHost() {
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [accounts, setAccounts] = useState<PersonalAccount[]>([]);
  const [cards, setCards] = useState<PersonalCreditCard[]>([]);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const defaultAccountId = accounts[0]?.id ?? "";
  const defaultCardId = cards[0]?.id ?? "";

  async function refreshContext() {
    setIsLoadingContext(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setAccounts([]);
      setCards([]);
      setIsLoadingContext(false);
      return;
    }

    const [accountsResult, cardsResult] = await Promise.all([
      supabase
        .from("personal_accounts")
        .select("id, name, institution, current_balance")
        .eq("user_id", user.id)
        .eq("is_archived", false)
        .order("created_at", { ascending: true }),
      CreditCardApplicationService.loadCardOptions(user.id)
        .then(data => ({ data, error: null }))
        .catch(error => ({ data: null, error })),
    ]);

    if (accountsResult.error) {
      console.error("Erro ao carregar contas para modal financeiro:", accountsResult.error);
      toast.error(`Erro ao carregar contas: ${accountsResult.error.message}`);
      setAccounts([]);
    } else {
      setAccounts((accountsResult.data ?? []) as PersonalAccount[]);
    }

    if (cardsResult.error) {
      console.error("Erro ao carregar cartões para modal financeiro:", cardsResult.error);
      toast.error(`Erro ao carregar cartões: ${cardsResult.error.message}`);
      setCards([]);
    } else {
      setCards((cardsResult.data ?? []) as PersonalCreditCard[]);
    }

    setIsLoadingContext(false);
  }

  useEffect(() => {
    function handleFinancialAction(event: Event) {
      const action = normalizeAction((event as CustomEvent<{ action?: string }>).detail?.action);
      if (!action) return;
      setActiveModal(action);
      void refreshContext();
    }

    window.addEventListener(FINANCIAL_EVENT, handleFinancialAction);
    return () => window.removeEventListener(FINANCIAL_EVENT, handleFinancialAction);
  }, []);

  useEffect(() => {
    if (!activeModal) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setActiveModal(null);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModal]);

  async function finishSave(
    message: string,
    options: {
      action: FinancialChangeAction;
      amount?: number;
      accountName?: string;
      description?: string;
      publish?: boolean;
    }
  ) {
    setIsSaving(false);
    setActiveModal(null);
    if (options.publish !== false) {
      publishFinancialExperienceUpdate({
        source: "financial-quick-action",
        action: options.action,
        surfaces: ["accounts", "cards", "dashboard", "financial", "invoice", "net-worth"],
        amount: options.amount,
        accountName: options.accountName,
      });
    }
    await queryClient.invalidateQueries();
    toast.success(message, { description: options.description });
  }

  async function createExpense(payload: ExpensePayload) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Usuário não autenticado.");
      return;
    }

    const recurrenceType =
      payload.recurrenceMode === "single"
        ? "single"
        : payload.repeatKind === "installment"
          ? "installment"
          : "recurring";
    const repeatCount =
      recurrenceType === "installment"
        ? Math.max(1, payload.installments - payload.installmentStart + 1)
        : getRepeatCount(payload.recurrenceMode, payload.repeatQuantity);
    const installmentAmounts =
      recurrenceType === "installment"
        ? getInstallmentAmounts(payload.amount, payload.installments, payload.amountMode).slice(
            payload.installmentStart - 1
          )
        : Array.from({ length: repeatCount }, () => payload.amount);
    const seriesEndDate = shiftDate(
      payload.transactionDate,
      Math.max(0, installmentAmounts.length - 1),
      payload.periodicity
    );
    let seriesId: string | null = null;

    setIsSaving(true);

    if (recurrenceType !== "single" && installmentAmounts.length > 1) {
      const { data: series, error: seriesError } = await supabase
        .from("personal_expense_series")
        .insert({
          user_id: user.id,
          expense_type: "variable",
          main_category: payload.category,
          sub_category: payload.subcategory,
          description: payload.description,
          amount: payload.amount,
          recurrence_type: recurrenceType,
          frequency:
            payload.periodicity === "yearly"
              ? "yearly"
              : payload.periodicity === "weekly"
                ? "custom"
                : "monthly",
          interval_months: 1,
          current_installment:
            recurrenceType === "installment" ? payload.installmentStart : null,
          total_installments:
            recurrenceType === "installment" ? payload.installments : null,
          start_month: dateToMonth(payload.transactionDate),
          end_month: dateToMonth(seriesEndDate),
        })
        .select("id")
        .single();

      if (seriesError) {
        setIsSaving(false);
        toast.error(`Erro ao criar série da despesa: ${seriesError.message}`);
        return;
      }

      seriesId = series.id;
    }

    const rows = installmentAmounts.map((installmentAmount, index) => {
      const installmentNumber = payload.repeatKind === "installment" ? payload.installmentStart + index : null;
      const dueDate = shiftDate(payload.dueDate, index, payload.periodicity);
      const transactionDate = shiftDate(payload.transactionDate, index, payload.periodicity);
      return {
        user_id: user.id,
        due_date: dueDate,
        transaction_at: toTransactionAt(transactionDate, payload.transactionTime),
        expense_month: dateToMonth(transactionDate),
        main_category: payload.category,
        sub_category: payload.subcategory,
        description:
          recurrenceType === "installment" && installmentNumber
            ? `${payload.description} ${installmentNumber}/${payload.installments}`
            : payload.description,
        amount: installmentAmount,
        recurrence_type: recurrenceType,
        status: payload.isPaid ? "paid" : "pending",
        paid_at: payload.isPaid ? new Date().toISOString() : null,
        paid_from_account_id: payload.accountId || null,
        series_id: seriesId,
        installment_number: installmentNumber,
        total_installments: recurrenceType === "installment" ? payload.installments : null,
      };
    });

    const { error } =
      rows.length === 1 && !seriesId
        ? await CanonicalFinancialCommandService.applyTransaction({
            operation: "create",
            sourceTable: "personal_expenses_variable",
            values: rows[0],
          })
        : await supabase.from("personal_expenses_variable").insert(rows);

    if (error) {
      if (seriesId) {
        await supabase.from("personal_expense_series").delete().eq("id", seriesId);
      }
      setIsSaving(false);
      toast.error(`Erro ao cadastrar despesa: ${error.message}`);
      return;
    }

    const account = accounts.find((item) => item.id === payload.accountId);
    await finishSave(
      rows.length > 1 ? `${rows.length} despesas cadastradas.` : "Despesa cadastrada.",
      {
        action: "transaction-created",
        amount: payload.amount,
        accountName: account?.name,
        description: `${formatBrl(payload.amount)}${account ? ` · ${account.name}` : ""}`,
      }
    );
  }

  async function createIncome(payload: IncomePayload) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Usuário não autenticado.");
      return;
    }

    const repeatCount = getRepeatCount(payload.recurrenceMode, payload.repeatQuantity);
    const rows = Array.from({ length: repeatCount }, (_, index) => {
      const dueDate = shiftDate(payload.dueDate, index, payload.periodicity);
      const transactionDate = shiftDate(payload.transactionDate, index, payload.periodicity);
      return {
        user_id: user.id,
        income_month: dateToMonth(transactionDate),
        due_date: dueDate,
        transaction_at: toTransactionAt(transactionDate, payload.transactionTime),
        category: payload.subcategory || payload.category,
        amount: payload.amount,
        description: payload.description || null,
      };
    });

    setIsSaving(true);
    const { error } =
      rows.length === 1
        ? await CanonicalFinancialCommandService.applyTransaction({
            operation: "create",
            sourceTable: "personal_incomes",
            values: rows[0],
          })
        : await supabase.from("personal_incomes").insert(rows);

    if (error) {
      setIsSaving(false);
      toast.error(`Erro ao cadastrar receita: ${error.message}`);
      return;
    }

    const account = accounts.find((item) => item.id === payload.accountId);
    await finishSave(
      rows.length > 1 ? `${rows.length} receitas cadastradas.` : "Receita cadastrada.",
      {
        action: "transaction-created",
        amount: payload.amount,
        accountName: account?.name,
        description: `${formatBrl(payload.amount)}${account ? ` · ${account.name}` : ""}`,
      }
    );
  }

  async function createCardExpense(payload: CardExpensePayload) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Usuário não autenticado.");
      return;
    }
    if (!payload.cardId) {
      toast.error("Cadastre ou selecione um cartão.");
      return;
    }
    const card = cards.find((item) => item.id === payload.cardId);
    if (!card) {
      toast.error("Cartão não encontrado.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await createCreditCardPurchaseWithInvoices({
        userId: user.id,
        card,
        description: payload.description,
        category: payload.category,
        subcategory: payload.subcategory,
        amount: payload.amount,
        purchaseDate: payload.purchaseDate,
        mode: payload.installments > 1 ? "installment" : "single",
        installments: payload.installments,
        installmentStart: payload.installmentStart,
        amountMode: payload.amountMode,
      });

      await finishSave(
        result.rows.length > 1
          ? `${result.rows.length} parcelas do cartão cadastradas.`
          : "Despesa de cartão cadastrada.",
        {
          action: "purchase-created",
          amount: payload.amount,
          description: `${formatBrl(payload.amount)} · ${card.name}`,
          publish: false,
        }
      );
    } catch (error) {
      setIsSaving(false);
      toast.error(
        `Erro ao cadastrar despesa do cartão: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  async function createTransfer(payload: TransferPayload) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Usuário não autenticado.");
      return;
    }
    if (!payload.fromAccountId || !payload.toAccountId) {
      toast.error("Selecione conta de origem e destino.");
      return;
    }
    if (payload.fromAccountId === payload.toAccountId) {
      toast.error("A origem e o destino precisam ser diferentes.");
      return;
    }

    const from = accounts.find((account) => account.id === payload.fromAccountId);
    const to = accounts.find((account) => account.id === payload.toAccountId);
    if (!from || !to) {
      toast.error("Conta de origem ou destino não encontrada.");
      return;
    }
    if (!payload.isPaid) {
      toast.info("Transferência pendente anotada visualmente. Efetive para movimentar as contas.");
      return;
    }

    setIsSaving(true);
    const { error: transactionError } =
      await CanonicalFinancialCommandService.createTransfer({
        sourceAccountId: from.id,
        destinationAccountId: to.id,
        amount: payload.amount,
        description: payload.description || `Transferência ${from.name} para ${to.name}`,
        transactionDate: payload.transactionDate,
      });

    if (transactionError) {
      setIsSaving(false);
      toast.error(`Erro ao registrar transferência: ${transactionError.message}`);
      return;
    }

    await finishSave("Transferência cadastrada.", {
      action: "transaction-created",
      amount: payload.amount,
      accountName: from.name,
      description: `${formatBrl(payload.amount)} · ${from.name} → ${to.name}`,
    });
  }

  const modalMeta = useMemo(() => {
    if (activeModal === "quick_expense") return { title: "Despesa rápida", eyebrow: "Lançamento simplificado", icon: <Zap className="h-5 w-5" /> };
    if (activeModal === "card_expense") return { title: "Despesa cartão", eyebrow: "Compra no cartão", icon: <CreditCard className="h-5 w-5" /> };
    if (activeModal === "income") return { title: "Nova Receita", eyebrow: "Entrada financeira", icon: <TrendingUp className="h-5 w-5" /> };
    if (activeModal === "transfer") return { title: "Nova Transferência", eyebrow: "Entre contas", icon: <ArrowLeftRight className="h-5 w-5" /> };
    return { title: "Nova Despesa", eyebrow: "Saída financeira", icon: <ReceiptText className="h-5 w-5" /> };
  }, [activeModal]);

  if (!activeModal) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex overscroll-contain bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setActiveModal(null);
      }}
    >
      <section className="mt-auto flex max-h-[94vh] w-full flex-col overflow-hidden rounded-b-none border border-gold/15 bg-[oklch(0.115_0.015_260_/_0.98)] shadow-[0_24px_90px_oklch(0.03_0.02_260_/_0.55)] sm:mt-0 sm:max-w-5xl sm:rounded-[1.35rem]">
        <header className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4 sm:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gold/15 text-gold">
              {modalMeta.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-gold">
                {modalMeta.eyebrow}
              </p>
              <h2 className="mt-1 truncate font-display text-2xl font-bold text-white">{modalMeta.title}</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActiveModal(null)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 transition-colors hover:border-gold/30 hover:text-gold"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-7">
          {isLoadingContext ? (
            <div className="rounded-2xl border border-gold/10 bg-white/[0.03] p-4 text-sm text-slate-300">
              Carregando dados financeiros...
            </div>
          ) : activeModal === "income" ? (
            <IncomeModal isSaving={isSaving} onSubmit={createIncome} />
          ) : activeModal === "quick_expense" ? (
            <QuickExpenseModal defaultAccountId={defaultAccountId} isSaving={isSaving} onSubmit={createExpense} />
          ) : activeModal === "card_expense" ? (
            <CardExpenseModal cards={cards} defaultCardId={defaultCardId} isSaving={isSaving} onSubmit={createCardExpense} />
          ) : activeModal === "transfer" ? (
            <TransferModal accounts={accounts} defaultFromAccountId={defaultAccountId} isSaving={isSaving} onSubmit={createTransfer} />
          ) : (
            <ExpenseModal accounts={accounts} defaultAccountId={defaultAccountId} isSaving={isSaving} onSubmit={createExpense} />
          )}
        </div>
      </section>
    </div>
  );
}

function ExpenseModal({
  accounts,
  defaultAccountId,
  isSaving,
  onSubmit,
}: {
  accounts: PersonalAccount[];
  defaultAccountId: string;
  isSaving: boolean;
  onSubmit: (payload: ExpensePayload) => Promise<void>;
}) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("alimentacao");
  const [subcategory, setSubcategory] = useState("");
  const [accountId, setAccountId] = useState(defaultAccountId);
  const [dueDate, setDueDate] = useState(todayIso());
  const [transactionDate, setTransactionDate] = useState(todayIso());
  const [transactionTime, setTransactionTime] = useState(currentTime());
  const [isPaid, setIsPaid] = useState(true);
  const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>("single");
  const [repeatKind, setRepeatKind] = useState<ExpenseRepeatKind>("repeat");
  const [repeatQuantity, setRepeatQuantity] = useState("2");
  const [periodicity, setPeriodicity] = useState<Periodicity>("monthly");
  const [installments, setInstallments] = useState("2");
  const [installmentStart, setInstallmentStart] = useState("1");
  const [amountMode, setAmountMode] = useState<AmountMode>("total");

  useEffect(() => setAccountId(defaultAccountId), [defaultAccountId]);

  const parsedAmount = amountFromInput(amount);
  const recurrenceSummary = buildExpenseRecurrenceSummary({
    recurrenceMode,
    repeatKind,
    repeatQuantity: Number(repeatQuantity),
    periodicity,
    installments: Number(installments),
    installmentStart: Number(installmentStart),
    amount: parsedAmount,
    amountMode,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = amountFromInput(amount);
    const count = Number(installments);
    const start = Number(installmentStart);
    if (!description.trim() || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Informe descrição e valor válido.");
      return;
    }
    if (repeatKind === "installment" && recurrenceMode !== "single" && (!Number.isInteger(count) || !Number.isInteger(start) || start < 1 || start > count)) {
      toast.error("Informe parcelas válidas.");
      return;
    }
    void onSubmit({
      description: description.trim(),
      amount: parsed,
      category,
      subcategory: subcategory.trim() || description.trim(),
      dueDate,
      transactionDate,
      transactionTime,
      isPaid,
      accountId,
      recurrenceMode,
      repeatKind,
      repeatQuantity: Math.max(1, Number(repeatQuantity) || 1),
      periodicity,
      installments: Math.max(1, count || 1),
      installmentStart: Math.max(1, start || 1),
      amountMode,
    });
  }

  return (
    <FinancialForm
      onSubmit={submit}
      footer={<SubmitButton icon={<ReceiptText className="h-4 w-4" />} isSaving={isSaving} label="Salvar despesa" />}
    >
      <FormColumn>
        <TextField icon={<ReceiptText className="h-4 w-4" />} value={description} onChange={setDescription} placeholder="Descrição" />
        <AmountField value={amount} onChange={setAmount} />
        <RecurrenceField value={recurrenceMode} onChange={setRecurrenceMode} />
        {recurrenceMode !== "single" ? (
          <RepeatPanel
            allowInstallment
            amount={parsedAmount}
            amountMode={amountMode}
            installmentStart={installmentStart}
            installments={installments}
            periodicity={periodicity}
            repeatKind={repeatKind}
            repeatQuantity={repeatQuantity}
            summary={recurrenceSummary}
            onAmountModeChange={setAmountMode}
            onInstallmentStartChange={setInstallmentStart}
            onInstallmentsChange={setInstallments}
            onPeriodicityChange={setPeriodicity}
            onRepeatKindChange={setRepeatKind}
            onRepeatQuantityChange={setRepeatQuantity}
          />
        ) : null}
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data de vencimento" value={dueDate} onChange={setDueDate} />
        <ToggleRow icon={<CheckCircle2 className="h-4 w-4" />} label="Efetivada" checked={isPaid} onChange={setIsPaid} />
        <SelectField icon={<Tags className="h-4 w-4" />} label="Categoria" value={category} onChange={setCategory} options={expenseCategoryOptions} />
        <TextField icon={<Tags className="h-4 w-4" />} value={subcategory} onChange={setSubcategory} placeholder="Subcategoria" />
        <SelectField icon={<Landmark className="h-4 w-4" />} label="Conta" value={accountId} onChange={setAccountId} options={[{ label: "Conta a definir", value: "" }, ...accounts.map((account) => ({ label: account.name, value: account.id }))]} />
      </FormColumn>
      <FormColumn>
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data do lançamento" value={transactionDate} onChange={setTransactionDate} />
        <TimeField icon={<Clock className="h-4 w-4" />} label="Hora do lançamento" value={transactionTime} onChange={setTransactionTime} />
      </FormColumn>

    </FinancialForm>
  );
}

function QuickExpenseModal({
  defaultAccountId,
  isSaving,
  onSubmit,
}: {
  defaultAccountId: string;
  isSaving: boolean;
  onSubmit: (payload: ExpensePayload) => Promise<void>;
}) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [forceDebit, setForceDebit] = useState(false);
  const [afterSave, setAfterSave] = useState<QuickAfterSave>("none");
  const [showHelp, setShowHelp] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = amountFromInput(amount);
    if (!description.trim() || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Informe estabelecimento/chave e valor válido.");
      return;
    }
    void onSubmit({
      description: description.trim(),
      amount: parsed,
      category: "alimentacao",
      subcategory: description.trim(),
      dueDate: todayIso(),
      transactionDate: todayIso(),
      transactionTime: currentTime(),
      isPaid: forceDebit,
      accountId: defaultAccountId,
      recurrenceMode: "single",
      repeatKind: "repeat",
      repeatQuantity: 1,
      periodicity: "monthly",
      installments: 1,
      installmentStart: 1,
      amountMode: "total",
    }).then(() => {
      if (afterSave === "edit") toast.info("Edição após salvar será conectada ao editor completo na próxima etapa.");
      if (afterSave === "details") toast.info("Detalhes após salvar será conectado ao painel de detalhes na próxima etapa.");
    });
  }

  return (
    <>
      <form onSubmit={submit} className="mx-auto max-w-xl space-y-4">
        <TextField icon={<Zap className="h-4 w-4" />} value={description} onChange={setDescription} placeholder="Estabelecimento ou chave" rightSlot={<HelpButton onClick={() => setShowHelp(true)} />} />
        <AmountField value={amount} onChange={setAmount} />
        <ToggleRow icon={<Wallet className="h-4 w-4" />} label="Forçar débito" checked={forceDebit} onChange={setForceDebit} />
        <SelectField icon={<Repeat className="h-4 w-4" />} label="Ação após salvar" value={afterSave} onChange={(value) => setAfterSave(value as QuickAfterSave)} options={[
          { label: "Nenhuma", value: "none" },
          { label: "Editar", value: "edit" },
          { label: "Abrir detalhes", value: "details" },
        ]} />
        <SubmitButton icon={<Zap className="h-4 w-4" />} isSaving={isSaving} label="Salvar despesa rápida" />
      </form>
      {showHelp ? <HelpDialog onClose={() => setShowHelp(false)} /> : null}
    </>
  );
}

function IncomeModal({
  isSaving,
  onSubmit,
}: {
  isSaving: boolean;
  onSubmit: (payload: IncomePayload) => Promise<void>;
}) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("salario");
  const [subcategory, setSubcategory] = useState("");
  const [dueDate, setDueDate] = useState(todayIso());
  const [transactionDate, setTransactionDate] = useState(todayIso());
  const [transactionTime, setTransactionTime] = useState(currentTime());
  const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>("single");
  const [repeatQuantity, setRepeatQuantity] = useState("2");
  const [periodicity, setPeriodicity] = useState<Periodicity>("monthly");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = amountFromInput(amount);
    if (Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    void onSubmit({
      description: description.trim(),
      amount: parsed,
      category,
      subcategory: subcategory.trim(),
      dueDate,
      transactionDate,
      transactionTime,
      isPaid: true,
      accountId: "",
      recurrenceMode,
      repeatQuantity: Math.max(1, Number(repeatQuantity) || 1),
      periodicity,
    });
  }

  return (
    <FinancialForm
      onSubmit={submit}
      footer={<SubmitButton icon={<TrendingUp className="h-4 w-4" />} isSaving={isSaving} label="Salvar receita" tone="green" />}
    >
      <FormColumn>
        <TextField icon={<TrendingUp className="h-4 w-4" />} value={description} onChange={setDescription} placeholder="Descrição" />
        <AmountField value={amount} onChange={setAmount} />
        <RecurrenceField value={recurrenceMode} onChange={setRecurrenceMode} />
        {recurrenceMode !== "single" ? (
          <RepeatPanel
            amount={amountFromInput(amount)}
            amountMode="total"
            installmentStart="1"
            installments="1"
            periodicity={periodicity}
            repeatKind="repeat"
            repeatQuantity={repeatQuantity}
            summary={buildSimpleRecurrenceSummary(recurrenceMode, Number(repeatQuantity), periodicity)}
            onAmountModeChange={() => undefined}
            onInstallmentStartChange={() => undefined}
            onInstallmentsChange={() => undefined}
            onPeriodicityChange={setPeriodicity}
            onRepeatKindChange={() => undefined}
            onRepeatQuantityChange={setRepeatQuantity}
          />
        ) : null}
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data de vencimento" value={dueDate} onChange={setDueDate} />
        <SelectField icon={<Tags className="h-4 w-4" />} label="Categoria" value={category} onChange={setCategory} options={incomeCategoryOptions} />
        <TextField icon={<Tags className="h-4 w-4" />} value={subcategory} onChange={setSubcategory} placeholder="Subcategoria" />
      </FormColumn>
      <FormColumn>
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data do lançamento" value={transactionDate} onChange={setTransactionDate} />
        <TimeField icon={<Clock className="h-4 w-4" />} label="Hora do lançamento" value={transactionTime} onChange={setTransactionTime} />
      </FormColumn>
    </FinancialForm>
  );
}

function CardExpenseModal({
  cards,
  defaultCardId,
  isSaving,
  onSubmit,
}: {
  cards: PersonalCreditCard[];
  defaultCardId: string;
  isSaving: boolean;
  onSubmit: (payload: CardExpensePayload) => Promise<void>;
}) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("cartao_credito");
  const [subcategory, setSubcategory] = useState("");
  const [cardId, setCardId] = useState(defaultCardId);
  const [purchaseDate, setPurchaseDate] = useState(todayIso());
  const [invoiceMonth, setInvoiceMonth] = useState(currentMonth());
  const [installments, setInstallments] = useState("1");
  const [installmentStart, setInstallmentStart] = useState("1");
  const [amountMode, setAmountMode] = useState<AmountMode>("total");

  useEffect(() => setCardId(defaultCardId), [defaultCardId]);

  const parsedAmount = amountFromInput(amount);
  const amounts = getInstallmentAmounts(parsedAmount || 0, Math.max(1, Number(installments) || 1), amountMode);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = amountFromInput(amount);
    const count = Number(installments);
    const start = Number(installmentStart);
    if (!description.trim() || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Informe descrição e valor válido.");
      return;
    }
    if (!Number.isInteger(count) || count < 1 || !Number.isInteger(start) || start < 1 || start > count) {
      toast.error("Informe parcelas válidas.");
      return;
    }
    void onSubmit({
      description: description.trim(),
      amount: parsed,
      category,
      subcategory: subcategory.trim() || category,
      cardId,
      purchaseDate,
      invoiceMonth,
      installments: count,
      installmentStart: start,
      amountMode,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <FormColumn>
          <LabeledField label="Descrição">
            <input className={fieldClass()} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Descrição" />
          </LabeledField>
          <LabeledField label="Valor">
            <AmountField value={amount} onChange={setAmount} />
            <p className="mt-2 text-xs text-slate-300">Despesa de {formatBrl(parsedAmount || 0)}</p>
          </LabeledField>
          <LabeledField label="Recorrência">
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => { setInstallments("1"); setInstallmentStart("1"); }} className={`h-14 rounded-xl border px-3 text-left text-sm font-bold ${Number(installments) <= 1 ? "border-gold/40 bg-gold/15 text-gold" : "border-white/10 bg-white/[0.03] text-slate-200"}`}>
                Não recorrente
              </button>
              <button type="button" onClick={() => setInstallments((value) => Number(value) > 1 ? value : "2")} className={`h-14 rounded-xl border px-3 text-left text-sm font-bold ${Number(installments) > 1 ? "border-gold/40 bg-gold/15 text-gold" : "border-white/10 bg-white/[0.03] text-slate-200"}`}>
                Parcelar ou repetir
              </button>
              <button type="button" onClick={() => { setInstallments("12"); setInstallmentStart("1"); }} className="h-14 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-left text-sm font-bold text-slate-200">
                Fixa mensal
              </button>
            </div>
          </LabeledField>
          {Number(installments) > 1 ? (
            <LabeledField label="Parcelamento">
              <div className="grid gap-2 sm:grid-cols-3">
                <input className={fieldClass()} type="number" min="1" value={installmentStart} onChange={(event) => setInstallmentStart(event.target.value)} placeholder="Parcela inicial" />
                <input className={fieldClass()} type="number" min="2" value={installments} onChange={(event) => setInstallments(event.target.value)} placeholder="Total parcelas" />
                <select className={selectClass()} value={amountMode} onChange={(event) => setAmountMode(event.target.value as AmountMode)}>
                  <option value="total">Valor total</option>
                  <option value="installment">Valor parcela</option>
                </select>
              </div>
              <p className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-300">
                {`${Math.max(1, Number(installments) || 1)}x de ${formatBrl(amounts[0] ?? 0)} · Total ${formatBrl(amounts.reduce((sum, item) => sum + item, 0))}`}
              </p>
            </LabeledField>
          ) : null}
        </FormColumn>

        <FormColumn>
          <LabeledField label="Cartão de crédito">
            <select className={selectClass()} value={cardId} onChange={(event) => setCardId(event.target.value)}>
              <option value="">Selecione o cartão</option>
              {cards.map((card) => (
                <option key={card.id} value={card.id}>
                  {card.name}{card.bank_name ? ` · ${card.bank_name}` : ""}
                </option>
              ))}
            </select>
          </LabeledField>
          <LabeledField label="Fatura">
            <input className={fieldClass()} type="month" value={invoiceMonth} onChange={(event) => setInvoiceMonth(event.target.value)} />
          </LabeledField>
          <LabeledField label="Categoria">
            <select className={selectClass()} value={category} onChange={(event) => setCategory(event.target.value)}>
              {expenseCategoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </LabeledField>
          <LabeledField label="Subcategoria">
            <input className={fieldClass()} value={subcategory} onChange={(event) => setSubcategory(event.target.value)} placeholder="Opcional" />
          </LabeledField>
          <LabeledField label="Data da compra">
            <input className={fieldClass()} type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} />
          </LabeledField>
        </FormColumn>
      </div>
      <div className="sticky bottom-0 -mx-5 border-t border-white/10 bg-[oklch(0.115_0.015_260_/_0.96)] px-5 pt-4 sm:-mx-7 sm:px-7">
        <SubmitButton icon={<CreditCard className="h-4 w-4" />} isSaving={isSaving} label="Salvar despesa cartão" />
      </div>
    </form>
  );
}

function TransferModal({
  accounts,
  defaultFromAccountId,
  isSaving,
  onSubmit,
}: {
  accounts: PersonalAccount[];
  defaultFromAccountId: string;
  isSaving: boolean;
  onSubmit: (payload: TransferPayload) => Promise<void>;
}) {
  const defaultToAccountId = accounts.find((account) => account.id !== defaultFromAccountId)?.id ?? "";
  const [description, setDescription] = useState("Transferência");
  const [amount, setAmount] = useState("");
  const [fromAccountId, setFromAccountId] = useState(defaultFromAccountId);
  const [toAccountId, setToAccountId] = useState(defaultToAccountId);
  const [dueDate, setDueDate] = useState(todayIso());
  const [transactionDate, setTransactionDate] = useState(todayIso());
  const [transactionTime, setTransactionTime] = useState(currentTime());
  const [isPaid, setIsPaid] = useState(true);

  useEffect(() => {
    setFromAccountId(defaultFromAccountId);
    setToAccountId(accounts.find((account) => account.id !== defaultFromAccountId)?.id ?? "");
  }, [accounts, defaultFromAccountId]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = amountFromInput(amount);
    if (Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    void onSubmit({
      description: description.trim(),
      amount: parsed,
      fromAccountId,
      toAccountId,
      dueDate,
      transactionDate,
      transactionTime,
      isPaid,
      recurrenceMode: "single",
      repeatQuantity: 1,
      periodicity: "monthly",
    });
  }

  return (
    <FinancialForm
      onSubmit={submit}
      footer={<SubmitButton icon={<ArrowLeftRight className="h-4 w-4" />} isSaving={isSaving} label="Salvar transferência" />}
    >
      <FormColumn>
        <TextField icon={<ArrowLeftRight className="h-4 w-4" />} value={description} onChange={setDescription} placeholder="Descrição" />
        <AmountField value={amount} onChange={setAmount} />
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data de vencimento" value={dueDate} onChange={setDueDate} />
        <DateField icon={<Calendar className="h-4 w-4" />} label="Data do lançamento" value={transactionDate} onChange={setTransactionDate} />
        <TimeField icon={<Clock className="h-4 w-4" />} label="Hora do lançamento" value={transactionTime} onChange={setTransactionTime} />
        <ToggleRow icon={<CheckCircle2 className="h-4 w-4" />} label="Efetivada" checked={isPaid} onChange={setIsPaid} />
        <SelectField icon={<Landmark className="h-4 w-4" />} label="Conta origem" value={fromAccountId} onChange={setFromAccountId} options={[{ label: "Origem", value: "" }, ...accounts.map((account) => ({ label: account.name, value: account.id }))]} />
        <SelectField icon={<Wallet className="h-4 w-4" />} label="Conta destino" value={toAccountId} onChange={setToAccountId} options={[{ label: "Destino", value: "" }, ...accounts.map((account) => ({ label: account.name, value: account.id }))]} />
      </FormColumn>
    </FinancialForm>
  );
}

const expenseCategoryOptions = [
  { label: "Alimentação", value: "alimentacao" },
  { label: "Habitação", value: "habitacao" },
  { label: "Transporte", value: "transporte" },
  { label: "Saúde", value: "saude" },
  { label: "Educação", value: "educacao" },
  { label: "Pagamentos", value: "pagamentos" },
  { label: "Cartões de crédito", value: "cartao_credito" },
  { label: "Outros", value: "outros" },
];

const incomeCategoryOptions = [
  { label: "Salário", value: "salario" },
  { label: "Pix", value: "pix" },
  { label: "Freelance", value: "freelance" },
  { label: "Investimentos", value: "investimentos" },
  { label: "Venda", value: "venda" },
  { label: "Outros", value: "outros" },
];

function buildSimpleRecurrenceSummary(mode: RecurrenceMode, quantity: number, periodicity: Periodicity) {
  if (mode === "single") return "Lançamento único.";
  const count = mode === "monthly" ? 12 : Math.max(1, quantity || 1);
  const period = periodicity === "weekly" ? "semanal" : periodicity === "yearly" ? "anual" : "mensal";
  return `${count} lançamento(s), periodicidade ${period}.`;
}

function buildExpenseRecurrenceSummary({
  recurrenceMode,
  repeatKind,
  repeatQuantity,
  periodicity,
  installments,
  installmentStart,
  amount,
  amountMode,
}: {
  recurrenceMode: RecurrenceMode;
  repeatKind: ExpenseRepeatKind;
  repeatQuantity: number;
  periodicity: Periodicity;
  installments: number;
  installmentStart: number;
  amount: number;
  amountMode: AmountMode;
}) {
  if (recurrenceMode === "single") return "Lançamento único.";
  if (repeatKind !== "installment") return buildSimpleRecurrenceSummary(recurrenceMode, repeatQuantity, periodicity);
  if (!Number.isFinite(amount) || amount <= 0) return "Informe o valor para calcular parcelas.";
  if (installmentStart < 1 || installmentStart > installments) return "A parcela inicial precisa ficar dentro do total.";
  const amounts = getInstallmentAmounts(amount, installments, amountMode).slice(installmentStart - 1);
  const total = amounts.reduce((sum, item) => sum + item, 0);
  return `Da parcela ${installmentStart}/${installments} até ${installments}/${installments}: ${amounts.length}x de ${formatBrl(amounts[0] ?? 0)}. Total lançado: ${formatBrl(total)}.`;
}

function FinancialForm({
  children,
  footer,
  onSubmit,
}: {
  children: ReactNode;
  footer: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">{children}</div>
      <div className="sticky bottom-0 -mx-5 border-t border-white/10 bg-[oklch(0.115_0.015_260_/_0.96)] px-5 pt-4 sm:-mx-7 sm:px-7">
        {footer}
      </div>
    </form>
  );
}

function FormColumn({ children }: { children: ReactNode }) {
  return <div className="space-y-3">{children}</div>;
}

function LabeledField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <span className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function InputLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.025] p-2">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-black/25 text-gold">
        {icon}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function TextField({
  icon,
  value,
  onChange,
  placeholder,
  rightSlot,
}: {
  icon: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  rightSlot?: ReactNode;
}) {
  return (
    <InputLine icon={icon}>
      <div className="flex items-center gap-2">
        <input className={fieldClass("border-0 bg-transparent px-0 focus:border-0")} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
        {rightSlot}
      </div>
    </InputLine>
  );
}

function AmountField({
  value,
  onChange,
  label = "Valor",
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

  return (
    <>
      <InputLine icon={<Wallet className="h-4 w-4" />}>
        <div className="flex items-center gap-2">
          <input
            className={fieldClass("border-0 bg-transparent px-0 focus:border-0")}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            inputMode="decimal"
            placeholder={label === "Valor" ? "R$ 0,00" : label}
          />
          <button
            type="button"
            onClick={() => setIsCalculatorOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 hover:border-gold/30 hover:text-gold"
            aria-label="Abrir calculadora"
          >
            <Calculator className="h-4 w-4" />
          </button>
        </div>
      </InputLine>
      {isCalculatorOpen ? (
        <CalculatorDialog
          initialValue={amountFromInput(value) || 0}
          onClose={() => setIsCalculatorOpen(false)}
          onConfirm={(nextValue) => {
            onChange(amountToInput(nextValue));
            setIsCalculatorOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function DateField({
  icon,
  label,
  value,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function openPicker() {
    const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
  }

  return (
    <InputLine icon={icon}>
      <label className="relative grid cursor-pointer gap-1" onClick={openPicker}>
        <span className="text-xs text-slate-500">{label}</span>
        <span className="block text-sm text-white">
          {value ? formatDate(value) : "Selecionar data"}
        </span>
        <input
          ref={inputRef}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
        />
      </label>
    </InputLine>
  );
}

function TimeField({
  icon,
  label,
  value,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function openPicker() {
    const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
  }

  return (
    <InputLine icon={icon}>
      <label className="relative grid cursor-pointer gap-1" onClick={openPicker}>
        <span className="text-xs text-slate-500">{label}</span>
        <span className="block text-sm text-white">{value || "12:00"}</span>
        <input
          ref={inputRef}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          type="time"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
        />
      </label>
    </InputLine>
  );
}

function SelectField({
  icon,
  label,
  value,
  onChange,
  options,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
}) {
  return (
    <InputLine icon={icon}>
      <label className="grid gap-1">
        <span className="text-xs text-slate-500">{label}</span>
        <select className="w-full bg-transparent text-sm text-white outline-none" value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </InputLine>
  );
}

function RecurrenceField({
  value,
  onChange,
}: {
  value: RecurrenceMode;
  onChange: (value: RecurrenceMode) => void;
}) {
  return (
    <InputLine icon={<Repeat className="h-4 w-4" />}>
      <div className="grid grid-cols-3 gap-2">
        {[
          ["single", "Não recorrente"],
          ["repeat", "Repetir"],
          ["monthly", "Fixa mensal"],
        ].map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            onClick={() => onChange(mode as RecurrenceMode)}
            className={`h-10 rounded-xl border px-2 text-xs font-semibold transition-colors ${
              value === mode
                ? "border-gold/40 bg-gold/20 text-gold"
                : "border-white/10 bg-black/15 text-slate-300 hover:border-gold/20"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </InputLine>
  );
}

function RepeatPanel({
  allowInstallment = false,
  amount,
  amountMode,
  installmentStart,
  installments,
  periodicity,
  repeatKind,
  repeatQuantity,
  summary,
  onAmountModeChange,
  onInstallmentStartChange,
  onInstallmentsChange,
  onPeriodicityChange,
  onRepeatKindChange,
  onRepeatQuantityChange,
}: {
  allowInstallment?: boolean;
  amount: number;
  amountMode: AmountMode;
  installmentStart: string;
  installments: string;
  periodicity: Periodicity;
  repeatKind: ExpenseRepeatKind;
  repeatQuantity: string;
  summary: string;
  onAmountModeChange: (value: AmountMode) => void;
  onInstallmentStartChange: (value: string) => void;
  onInstallmentsChange: (value: string) => void;
  onPeriodicityChange: (value: Periodicity) => void;
  onRepeatKindChange: (value: ExpenseRepeatKind) => void;
  onRepeatQuantityChange: (value: string) => void;
}) {
  return (
    <div className="space-y-3 rounded-2xl border border-gold/15 bg-gold/[0.04] p-3">
      {allowInstallment ? (
        <div className="grid grid-cols-2 gap-2">
          {[
            ["repeat", "Repetição"],
            ["installment", "Parcelada"],
          ].map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => onRepeatKindChange(mode as ExpenseRepeatKind)}
              className={`h-10 rounded-xl border px-3 text-xs font-semibold ${
                repeatKind === mode ? "border-emerald-300/40 bg-emerald-300/15 text-emerald-200" : "border-white/10 bg-black/15 text-slate-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {repeatKind === "installment" && allowInstallment ? (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <input className={fieldClass()} type="number" min="1" value={installmentStart} onChange={(event) => onInstallmentStartChange(event.target.value)} placeholder="Parcela inicial" />
            <input className={fieldClass()} type="number" min="2" value={installments} onChange={(event) => onInstallmentsChange(event.target.value)} placeholder="Total parcelas" />
            <select className={selectClass()} value={amountMode} onChange={(event) => onAmountModeChange(event.target.value as AmountMode)}>
              <option value="total">Valor total</option>
              <option value="installment">Valor parcela</option>
            </select>
          </div>
          <p className="text-xs text-slate-300">{Number.isFinite(amount) ? summary : "Informe o valor para calcular parcelas."}</p>
        </>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <input className={fieldClass()} type="number" min="1" value={repeatQuantity} onChange={(event) => onRepeatQuantityChange(event.target.value)} placeholder="Quantidade" />
            <select className={selectClass()} value={periodicity} onChange={(event) => onPeriodicityChange(event.target.value as Periodicity)}>
              <option value="monthly">Mensal</option>
              <option value="weekly">Semanal</option>
              <option value="yearly">Anual</option>
            </select>
          </div>
          <p className="text-xs text-slate-300">{summary}</p>
        </>
      )}
    </div>
  );
}

function ToggleRow({
  icon,
  label,
  checked,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.025] p-2 text-sm font-semibold text-white">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-black/25 text-gold">
        {icon}
      </div>
      <span className="min-w-0 flex-1">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-gold"
      />
    </label>
  );
}

function HelpButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 hover:border-gold/30 hover:text-gold"
      aria-label="Ajuda"
    >
      <HelpCircle className="h-4 w-4" />
    </button>
  );
}

function HelpDialog({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/70 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[oklch(0.14_0.014_260)] p-5 shadow-2xl">
        <h3 className="text-lg font-bold text-white">Ajuda</h3>
        <p className="mt-4 text-sm leading-relaxed text-slate-200">
          Ao cadastrar uma despesa com uma palavra-chave ou local, as próximas com o mesmo texto herdarão automaticamente a conta, cartão e categorias da última registrada.
        </p>
        <button type="button" onClick={onClose} className="mt-5 h-10 rounded-xl bg-gold px-5 text-sm font-bold text-black">
          Ok
        </button>
      </div>
    </div>
  );
}

function CalculatorDialog({
  initialValue,
  onClose,
  onConfirm,
}: {
  initialValue: number;
  onClose: () => void;
  onConfirm: (value: number) => void;
}) {
  const [display, setDisplay] = useState(String(initialValue || 0).replace(".", ","));
  const [stored, setStored] = useState<number | null>(null);
  const [operation, setOperation] = useState<"+" | "-" | "*" | "/" | null>(null);

  function currentValue() {
    return amountFromInput(display) || 0;
  }

  function input(value: string) {
    setDisplay((previous) => {
      if (previous === "0" && value !== ",") return value;
      if (value === "," && previous.includes(",")) return previous;
      return `${previous}${value}`;
    });
  }

  function calculate(nextOperation?: typeof operation) {
    const current = currentValue();
    if (stored === null || !operation) {
      setStored(current);
      setOperation(nextOperation ?? null);
      setDisplay("0");
      return current;
    }
    const result =
      operation === "+"
        ? stored + current
        : operation === "-"
          ? stored - current
          : operation === "*"
            ? stored * current
            : current === 0
              ? stored
              : stored / current;
    setStored(result);
    setOperation(nextOperation ?? null);
    setDisplay(nextOperation ? "0" : amountToInput(result));
    return result;
  }

  return (
    <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/70 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="w-full max-w-sm rounded-[1.4rem] border border-white/10 bg-[oklch(0.14_0.014_260)] p-5 shadow-2xl">
        <div className="mb-4 rounded-2xl bg-black/25 p-4 text-right text-3xl font-bold text-white">{display}</div>
        <div className="grid grid-cols-4 gap-2">
          {["C", "%", "⌫", "/"].map((key) => (
            <CalcButton key={key} label={key} onClick={() => {
              if (key === "C") {
                setDisplay("0");
                setStored(null);
                setOperation(null);
              } else if (key === "⌫") {
                setDisplay((previous) => previous.length <= 1 ? "0" : previous.slice(0, -1));
              } else if (key === "%") {
                setDisplay(amountToInput(currentValue() / 100));
              } else {
                calculate("/");
              }
            }} />
          ))}
          {["7", "8", "9", "*", "4", "5", "6", "-", "1", "2", "3", "+", "00", "0", ","].map((key) => (
            <CalcButton key={key} label={key === "*" ? "x" : key} onClick={() => {
              if (key === "*" || key === "-" || key === "+") calculate(key as "*" | "-" | "+");
              else input(key);
            }} />
          ))}
          <button
            type="button"
            onClick={() => onConfirm(calculate(null))}
            className="h-14 rounded-2xl bg-gold text-xl font-black text-black"
          >
            ✓
          </button>
        </div>
      </div>
    </div>
  );
}

function CalcButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-14 rounded-2xl bg-white/[0.06] text-lg font-bold text-white transition-colors hover:bg-white/[0.1]"
    >
      {label}
    </button>
  );
}

function SubmitButton({
  icon,
  isSaving,
  label,
  tone = "gold",
}: {
  icon: ReactNode;
  isSaving: boolean;
  label: string;
  tone?: "gold" | "green";
}) {
  const className =
    tone === "green"
      ? "bg-emerald-400 text-[oklch(0.08_0.01_260)]"
      : "gradient-gold text-[oklch(0.08_0.01_260)]";

  return (
    <button
      type="submit"
      disabled={isSaving}
      className={`flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-black transition-opacity hover:opacity-95 disabled:opacity-60 ${className}`}
    >
      {icon}
      {isSaving ? "Salvando..." : label}
    </button>
  );
}
